import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, effect, signal } from '@angular/core';
import { $t } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { Subject } from 'rxjs';

import { LocalStorageService } from '@shared/services';

export interface AppConfig {
  preset?: string;
  primary?: string;
  surface?: string | undefined | null;
  darkTheme?: boolean;
  menuMode?: string;
  scale: number;
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  staticMenuMobileActive: boolean;
  isFullScreen: boolean;
  isDarkMode: boolean;
}

type DocumentType = Document & {
  mozCancelFullScreen?: () => void;
  webkitExitFullscreen?: () => void;
  msExitFullscreen?: () => void;
  mozRequestFullScreen?: () => void;
  startViewTransition(callback: () => void): { ready: Promise<void> };
};

type HtmlElementType = HTMLElement & {
  mozRequestFullScreen?: () => void;
  webkitRequestFullscreen?: () => void;
  msRequestFullscreen?: () => void;
};

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  _config: AppConfig = {
    preset: 'Aura',
    primary: 'blue',
    surface: 'slate',
    darkTheme: false,
    menuMode: 'static',
    scale: 12,
  };
  config = signal<AppConfig>(this._config);
  state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    staticMenuMobileActive: false,
    isFullScreen: false,
    isDarkMode: false,
  };
  transitionComplete = signal<boolean>(false);

  configUpdate = new Subject<AppConfig>();
  overlayOpen = new Subject<boolean | null>();
  initialized: boolean = false;
  configUpdate$ = this.configUpdate.asObservable();
  overlayOpen$ = this.overlayOpen.asObservable();
  elem: HtmlElementType;

  constructor(
    @Inject(DOCUMENT) private document: DocumentType,
    private localStorageService: LocalStorageService
  ) {
    this.elem = document.documentElement;

    const savedLayoutStateAndConfig = this.localStorageService.load(LocalStorageService.APP_LAYOUT_CONFIG);
    if (savedLayoutStateAndConfig) {
      this.state = savedLayoutStateAndConfig.state;
      this.config.set(savedLayoutStateAndConfig.config);
    }

    this.applyDarkModeClass(this.config());

    effect(() => {
      const config = this.config();
      this.changeScale(config.scale);
      this.onConfigUpdate();
    });

    effect(() => {
      const config = this.config();

      if (!this.initialized || !config) {
        this.initialized = true;
        return;
      }

      this.handleDarkModeTransition(config);
    });
  }

  onToggleDarkMode() {
    this.config.update((prev) => ({
      ...prev,
      darkTheme: !this.config().darkTheme,
    }));
    this.handleDarkModeTransition(this.config());
  }

  private handleDarkModeTransition(config: AppConfig): void {
    if ((document as DocumentType).startViewTransition) {
      this.startViewTransition(config);
    } else {
      this.applyDarkModeClass(config);
      this.onTransitionEnd();
    }
  }

  private startViewTransition(config: AppConfig): void {
    const transition = (document as DocumentType).startViewTransition(() => {
      this.applyDarkModeClass(config);
    });

    transition.ready
      .then(() => {
        this.onTransitionEnd();
      })
      .catch(() => {});
  }

  private applyDarkModeClass(config?: AppConfig): void {
    const _config = config || this.config();
    if (_config.darkTheme) {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }

  private onTransitionEnd() {
    this.transitionComplete.set(true);
    setTimeout(() => {
      this.transitionComplete.set(false);
    });
  }

  private saveState() {
    const stateToSave = {
      state: this.state,
      config: this.config(),
    };
    this.localStorageService.store(LocalStorageService.APP_LAYOUT_CONFIG, stateToSave);
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;
      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive = !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
    this.saveState();
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = { ...this.config() };
    this.configUpdate.next(this.config());
    this.saveState();
  }

  changeScale(value: number) {
    document.documentElement.style.fontSize = `${value}px`;
  }

  openFullscreen() {
    if (this.state.isFullScreen) {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    } else {
      if (this.elem.requestFullscreen) {
        this.elem!.requestFullscreen();
      } else if (this.elem.mozRequestFullScreen) {
        this.elem.mozRequestFullScreen();
      } else if (this.elem.webkitRequestFullscreen) {
        this.elem.webkitRequestFullscreen();
      } else if (this.elem.msRequestFullscreen) {
        this.elem.msRequestFullscreen();
      }
    }
    this.state.isFullScreen = !this.state.isFullScreen;
    this.saveState();
  }

  initPreset() {
    const surfacePalette = {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    };
    const presetExt = {
      semantic: {
        primary: Aura.primitive[this._config.primary],
        colorScheme: {
          light: {
            primary: {
              color: '{primary.500}',
              contrastColor: '#ffffff',
              hoverColor: '{primary.600}',
              activeColor: '{primary.700}',
            },
            highlight: {
              background: '{primary.50}',
              focusBackground: '{primary.100}',
              color: '{primary.700}',
              focusColor: '{primary.800}',
            },
          },
          dark: {
            primary: {
              color: '{primary.400}',
              contrastColor: '{surface.900}',
              hoverColor: '{primary.300}',
              activeColor: '{primary.200}',
            },
            highlight: {
              background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
              focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
              color: 'rgba(255,255,255,.87)',
              focusColor: 'rgba(255,255,255,.87)',
            },
          },
        },
      },
    };
    $t().preset(Aura).preset(presetExt).surfacePalette(surfacePalette).use({ useDefaultOptions: true });
  }
}
